const key = 'bb-recently-viewed-v22', limit = 12;
const note = 'Your recent finds in this browser.';

export function productPath(value) {
  try {
    const url = new URL(value, location.origin);
    const match = url.pathname.match(/^(\/[a-z]{2}(?:-[a-z]{2})?)?(?:\/collections\/[^/]+)?\/products\/([^/]+)\/?$/i);
    return url.origin === location.origin && match ? `${match[1] || ''}/products/${match[2]}` : null;
  } catch { return null; }
}
export function recentHistory(value, current) {
  const seen = new Set([current]);
  return (Array.isArray(value) ? value : []).flatMap(item => {
    const path = productPath(item);
    if (!path || seen.has(path)) return [];
    seen.add(path); return [path];
  }).slice(0, limit);
}

export class RecentlyViewed {
  constructor(element) {
    this.element = element; this.current = productPath(element.dataset.productUrl);
    this.track = element.querySelector('[data-recent-track]');
    this.next = element.querySelector('[data-recent-next]'); this.prev = element.querySelector('[data-recent-prev]');
    this.status = element.querySelector('[data-recent-status]'); this.retry = element.querySelector('[data-recent-retry]');
    this.clearButton = element.querySelector('[data-recent-clear]');
    this.cards = new Map(); this.history = []; this.order = [];
    this.events = new AbortController(); const options = {signal:this.events.signal};
    this.clearButton.addEventListener('click', () => this.clear(), options);
    this.retry.addEventListener('click', () => { clearTimeout(this.retryTimer); this.load(); }, options);
    this.next.addEventListener('click', () => this.move(1), options); this.prev.addEventListener('click', () => this.move(-1), options);
    this.track.addEventListener('scroll', () => {
      this.update();
      if (this.track.scrollLeft > 10 && this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 100) this.load();
    }, {...options,passive:true});
    this.track.addEventListener('keydown', event => {
      if(event.target === this.track && ['ArrowLeft','ArrowRight'].includes(event.key)) {
        event.preventDefault(); this.move(event.key === 'ArrowRight' ? 1 : -1);
      }
    }, options);
    document.addEventListener('visitorConsentCollected', () => this.consent(), options);
    window.addEventListener('storage', event => {
      if(this.allowed && (event.key === key || event.key === null)) this.sync(false);
    }, options);
    window.addEventListener('pageshow', event => {
      if(event.persisted) this.consent(true);
    }, options);
    this.resize = new ResizeObserver(() => this.update()); this.resize.observe(this.track);
    this.preparePrivacy(); this.update();
  }
  preparePrivacy() {
    if (this.events.signal.aborted) return;
    if (typeof this.consent() === 'boolean') return;
    if (!this.featureRequested && typeof window.Shopify?.loadFeatures === 'function') {
      this.featureRequested = true;
      window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.2'}], () => this.consent());
    }
    // A delayed Shopify API is not a refusal. Do not erase saved history while
    // it is loading, and do not collect anything until it gives permission.
    if ((this.privacyChecks || 0) < 12) {
      clearTimeout(this.privacyTimer); this.privacyChecks = (this.privacyChecks || 0) + 1;
      this.privacyTimer = setTimeout(() => this.preparePrivacy(), 500);
    }
  }
  consent(refresh = false) {
    if (this.events.signal.aborted) return;
    let allowed;
    try { allowed = window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.(); } catch { return; }
    if (allowed === false) { clearTimeout(this.privacyTimer); this.allowed = false; this.clear(); return false; }
    if (allowed !== true || !this.current) return;
    clearTimeout(this.privacyTimer);
    if (this.allowed && !refresh) return true;
    this.allowed = true; this.sync(true); return true;
  }
  sync(recordCurrent) {
    if (!this.allowed || this.events.signal.aborted) return;
    let stored;
    try { stored = localStorage.getItem(key); } catch { return; }
    let history;
    try { history = JSON.parse(stored); } catch { history = []; }
    const paths = recentHistory(history, this.current);
    if(recordCurrent) {
      try { localStorage.setItem(key, JSON.stringify([this.current,...paths].slice(0,limit))); } catch { /* Existing cards can still be displayed if storage becomes full. */ }
    }
    this.cancel(); this.order = paths;
    for(const path of this.cards.keys()) if(!paths.includes(path)) this.cards.delete(path);
    this.history = paths.filter(path => !this.cards.has(path)); this.render();
    this.element.hidden = paths.length === 0;
    this.retry.hidden = true; this.autoRetried = false;
    this.status.textContent = this.cards.size ? note : 'Loading your recently viewed cards…';
    if (!paths.length) return;
    if(this.inView || !('IntersectionObserver' in window)) this.load();
    else {
      this.visible?.disconnect();
      this.visible = new IntersectionObserver(entries => {
        if(entries.some(entry => entry.isIntersecting)) {
          this.inView = true; this.visible.disconnect(); this.load();
        }
      }, {rootMargin:'200px'});
      this.visible.observe(this.element);
    }
  }
  render() {
    const left = this.track.scrollLeft;
    this.track.replaceChildren(...this.order.flatMap(path => this.cards.has(path) ? [this.cards.get(path)] : []));
    this.track.scrollLeft = left; this.update();
  }
  async readCard(path, signal) {
    const request = new AbortController(), abort = () => request.abort();
    signal.addEventListener('abort', abort, {once:true});
    const timer = setTimeout(abort, 10000);
    try {
      const url = new URL(path, location.origin); url.searchParams.set('section_id','bb-recent-card');
      const response = await fetch(url, {credentials:'same-origin',signal:request.signal});
      if(response.status === 404 || response.status === 410) return {path,missing:true};
      if(!response.ok) return {path,retry:true};
      const parsed = new DOMParser().parseFromString(await response.text(),'text/html');
      const card = parsed.querySelector('[data-bb-recent-card-feed] [data-bb-card]');
      const cardPath = productPath(card?.querySelector('h3 a')?.getAttribute('href'));
      if(!card || !/^\d+$/.test(card.dataset.productId || '') || cardPath !== path) return {path,retry:true};
      card.querySelectorAll('script').forEach(node => node.remove());
      return {path,card};
    } catch { return {path,retry:true}; }
    finally { clearTimeout(timer); signal.removeEventListener('abort',abort); }
  }
  async load() {
    if (this.loading || !this.allowed || !this.history.length || this.events.signal.aborted) return;
    this.loading = true; this.track.setAttribute('aria-busy','true'); this.retry.hidden = true;
    const request = new AbortController(); this.request = request;
    const pending = this.history.splice(0), failed = []; let successes = 0;
    if(!this.cards.size) this.status.textContent = 'Loading your recently viewed cards…';
    try {
      // Only one four-card group at a time, with at most two requests in flight.
      while(pending.length && successes < 4) {
        const batch = pending.splice(0,Math.min(2,4-successes));
        const results = await Promise.all(batch.map(path => this.readCard(path,request.signal)));
        if(request.signal.aborted || this.request !== request || !this.allowed) return;
        for(const result of results) {
          if(result.card) { this.cards.set(result.path,result.card); successes++; }
          else if(result.retry) failed.push(result.path);
          else this.order = this.order.filter(path => path !== result.path);
        }
      }
      this.history = [...pending,...failed]; this.render();
      // Keep loaded cards and the row in place during network errors. Failed
      // paths stay queued so a retry cannot silently lose recently viewed cards.
      this.retry.hidden = failed.length === 0;
      this.status.textContent = failed.length ? 'Some recent cards couldn’t load. Try again.' : this.cards.size ? note : 'Those recent listings are no longer available. Browse more cards to start a new list.';
      if(successes) document.dispatchEvent(new Event('bb:cards-loaded'));
      if(failed.length && !this.autoRetried) {
        this.autoRetried = true;
        this.retryTimer = setTimeout(() => this.load(),1000);
      }
    } finally {
      if(this.request === request) { this.loading = false; this.request = null; this.track.setAttribute('aria-busy','false'); this.update(); }
    }
  }
  async move(direction) {
    const max = this.track.scrollWidth-this.track.clientWidth;
    if(direction > 0 && this.track.scrollLeft >= max-40 && this.history.length) await this.load();
    const step = (this.track.firstElementChild?.getBoundingClientRect().width || this.track.clientWidth) + 20;
    this.track.scrollBy({left:direction*step,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
  }
  update() {
    const max = this.track.scrollWidth-this.track.clientWidth;
    this.prev.disabled = this.track.scrollLeft < 2;
    this.next.disabled = !this.history.length && this.track.scrollLeft >= max-2;
  }
  cancel() {
    this.request?.abort(); this.request = null; this.loading = false;
    clearTimeout(this.retryTimer); this.track.setAttribute('aria-busy','false');
  }
  clear() {
    this.cancel(); this.visible?.disconnect();
    try { localStorage.removeItem(key); } catch { /* Storage may be unavailable. */ }
    this.history = []; this.order = []; this.cards.clear(); this.track.replaceChildren();
    this.element.hidden = true; this.retry.hidden = true; this.update();
  }
  destroy() {
    this.cancel(); clearTimeout(this.privacyTimer); this.visible?.disconnect(); this.resize.disconnect(); this.events.abort();
  }
}

const instances = new WeakMap();
function sections(root) {
  return [...(root.matches?.('[data-bb-recently-viewed]') ? [root] : []),...root.querySelectorAll('[data-bb-recently-viewed]')];
}
function initialise(root = document) {
  sections(root).forEach(element => { if(!instances.has(element)) instances.set(element,new RecentlyViewed(element)); });
}
if(document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => initialise()); else initialise();
window.addEventListener('pageshow',() => initialise());
document.addEventListener('shopify:section:load',event => initialise(event.target));
document.addEventListener('shopify:section:unload',event => sections(event.target).forEach(element => { instances.get(element)?.destroy(); instances.delete(element); }));
